import { useEffect, useMemo, type ReactElement } from 'react';
import { Box, Center, Loader, Stack, Text } from '@mantine/core';
import { ScanFace } from 'lucide-react';
import PageLevelFilterRow from '@/shared/surfaces/PageLevelFilterRow';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import SurfaceAssistScope, { SurfaceAssistToolbar } from '@/shared/surfaces/assist/SurfaceAssistScope';
import { useAssistFilters } from '@/shared/surfaces/assist/AssistSurfaceProvider';
import SideRailPageLayout from '@/shared/surfaces/SideRailPageLayout';
import TopicSummaryPanel from '@/shared/surfaces/TopicSummaryPanel';
import DerivedInsightsPanel from '@/shared/surfaces/DerivedInsightsPanel';
import { useSurface } from '@/shared/surfaces/useSurface';
import { useGlobalFilterKey } from '@/features/global-filters/useGlobalFilters';
import TopicNotFound from '@/shared/surfaces/TopicNotFound';
import type { AnalyticsSurface } from '@/shared/surfaces/AnalyticsSurface';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import HeatMap, { type HeatMapCell, type HeatMapColumn, type HeatMapRow } from '@/design/HeatMap/HeatMap';
import type { SignalHeat } from '@/shared/signals/SignalTypes';
import { GovernanceRiskDensityViewModel } from './GovernanceRiskDensityViewModel';

interface HeatMapModel {
    rows: HeatMapRow[];
    columns: HeatMapColumn[];
    cellAt: (rowKey: string, columnKey: string) => HeatMapCell;
    maxValue: number;
}

/** Adapts the department × work-relatedness matrix to the shared {@link HeatMap} cross-tab (the same primitive the Adoption heatmap uses). */
function toHeatMapModel(heat: SignalHeat): HeatMapModel {
    const columns: HeatMapColumn[] = heat.columns.map((col) => ({ key: col, label: col }));
    const rows: HeatMapRow[] = heat.rows.map((row) => ({ key: row.label, label: row.label, total: row.values.reduce((sum, value) => sum + value, 0) }));
    const lookup = new Map(heat.rows.map((row) => [row.label, new Map(heat.columns.map((col, index) => [col, row.values[index] ?? 0]))]));
    const maxValue = heat.rows.reduce((max, row) => Math.max(max, ...row.values), 0);
    return {
        rows,
        columns,
        cellAt: (rowKey, columnKey) => ({ value: lookup.get(rowKey)?.get(columnKey) ?? 0 }),
        maxValue,
    };
}

/** Governance › Risk Density: where governance exposure clusters — a department × work-relatedness heatmap with a summary + insights rail. */
export default function GovernanceRiskDensityPage(): ReactElement {
    const surface = useSurface('governance');
    if (surface === undefined) return <TopicNotFound />;
    return (
        <SurfaceAssistScope surface={surface}>
            <RiskDensityBody surface={surface} />
        </SurfaceAssistScope>
    );
}

function RiskDensityBody({ surface }: { surface: AnalyticsSurface }): ReactElement {
    const vm = useViewModel(GovernanceRiskDensityViewModel);
    const ambientFilters = useAssistFilters();
    const filters = useMemo(() => [...ambientFilters], [ambientFilters]);
    const filterKey = useGlobalFilterKey();

    useEffect(() => {
        void vm.load(filters);
    }, [vm, filters, filterKey]);

    const model = useMemo(() => (vm.heat === null ? null : toHeatMapModel(vm.heat)), [vm.heat]);

    return (
        <Box pb="xl">
            <PageLevelFilterRow assist={<SurfaceAssistToolbar surface={surface} />} />

            <PageHeaderComponent
                icon={ScanFace}
                eyebrow="Governance"
                title="Risk Density"
                subtitle="Where governance exposure clusters — the concentration of AI activity across department and work-relatedness."
            />

            <SideRailPageLayout
                rail={
                    <Stack gap="md">
                        <TopicSummaryPanel stats={vm.summary} />
                        <DerivedInsightsPanel callouts={vm.callouts} accent="rose" />
                    </Stack>
                }
            >
                {!vm.loaded ?
                    <Center py="xl">
                        <Loader size="sm" />
                    </Center>
                : model === null ?
                    <Text size="sm" c="dimmed" py="xl" ta="center">
                        No governance activity to chart for the selected range.
                    </Text>
                :   <HeatMap
                        rows={model.rows}
                        columns={model.columns}
                        cellAt={model.cellAt}
                        maxValue={model.maxValue}
                        accent="rose"
                        cornerLabel="Department"
                        metricNoun="prompts"
                        formatValue={(value) => value.toLocaleString('en-US')}
                    />
                }
            </SideRailPageLayout>
        </Box>
    );
}
